import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays } from 'date-fns';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { TextBlock } from '../../../Atoms/Text';
import { getCurrentUser } from '../../../../store/User';
import { Association, createTicket, TicketInquiryType, TicketStatus, TicketType } from '../../../../store/Ticket';
import {
  Asset,
  fetchAssetsByRpmAcctId,
  getAssetsByRpmAcctSub,
  getAssetsByRpmAcctDrawerSub
} from '../../../../store/Asset';
import {
  fetchRpmAccountsByAsset,
  fetchRpmAccountsByPAsset,
  getCruxRpmAccountsByAssetSubject,
  getCruxRpmAccountsByAssetDrawerSubject,
  getTallyRpmAccountsByPAssetSubject,
  getTallyRpmAccountsByPAssetDrawerSubject,
  RpmAccount
} from '../../../../store/RpmAccount';
import {
  fetchProvisionalAssetsByRpmAcctId,
  getProvisionalAssetsByRpmAcctSub,
  getProvisionalAssetsByRpmAcctDrawerSub,
  ProvisionalAsset
} from '../../../../store/ProvisionalAsset';
import { getRpmRunSubject } from '../../../../store/RpmRun';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { fetchSupplierMapsByRpmAcctSupplierId, SupplierMap } from '../../../../store/SupplierMap';
import { getUTCRunDate } from '../../../../utils/helpers';
import {
  fetchTsdMaps,
  getTsdMapByRpmSource,
  getTsdMapBySfTsdId,
  getTsdMapBySfTsdName,
  TsdMap
} from '../../../../store/TsdMap';
import { clearDrawerState } from '../../../../store/Drawer';
import { LinkedReference } from './LinkedReference';
import { FormData, TicketFormFields } from './TicketFormFields';

export interface NewTicketDialogParams {
  open: boolean;
  setOpen: (state: boolean) => void;
  association?: Association;
  objectData?: Asset | RpmAccount | ProvisionalAsset;
  objectType: 'asset' | 'provisionalAsset' | 'rpmAccount' | 'customer';
  objectLinkId?: string;
  objectLinkDisplay?: string;
  drawer?: boolean;
}

export const NewTicketDialog = ({
  open,
  setOpen,
  association,
  objectData,
  objectType,
  objectLinkId,
  objectLinkDisplay,
  drawer = false
}: NewTicketDialogParams) => {
  const { setSnack } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    ticketType: TicketType.ASSETS_AND_RPM_ACCOUNTS,
    inquiryType: TicketInquiryType.ACCOUNT_DROPPED_OFF,
    supplier: '',
    supplierName: '',
    tsd: '',
    tsdName: '',
    firstRun: null,
    receivedDate: new Date().toISOString(),
    proactive: true,
    referenceLinkedObjects: true
  });
  const [firstRunDate, setFirstRunDate] = useState<Date | null>(null);
  const [receivedDate, setReceivedDate] = useState<Date | null>(new Date());
  const [otherRpmAssociations, setOtherRpmAssociations] = useState<RpmAccount[]>([]);
  const [otherAssetAssociations, setOtherAssetAssociations] = useState<Asset[]>([]);
  const [otherPAAssociations, setOtherPAAssociations] = useState<ProvisionalAsset[]>([]);
  const [showSupplierSearch, setShowSupplierSearch] = useState<boolean>(false);
  const [showSupplierSelect, setShowSupplierSelect] = useState<boolean>(false);
  const navigate = useNavigate();

  const [supplierOptions, setSupplierOptions] = useState<{ value: string; label: string }[]>([]);
  const user = getCurrentUser();

  const handleClose = () => {
    if (!isSubmitting) setOpen(false);
  };

  const buildAssociations = (associations: Association[]) => {
    switch (objectType) {
      case 'asset':
      case 'provisionalAsset':
        otherRpmAssociations.forEach((rpmAcct) => {
          associations.push({
            type: 'rmpAccount',
            body: {
              entityId: String(rpmAcct.accountId)
            }
          });
        });
        break;
      case 'rpmAccount':
        otherAssetAssociations.forEach((asset) => {
          associations.push({
            type: 'asset',
            body: {
              entityId: asset.id
            }
          });
        });
        otherPAAssociations.forEach((pa) => {
          associations.push({
            type: 'provisional_asset',
            body: {
              entityId: pa.id
            }
          });
        });
        break;
      default:
        break;
    }
  };

  const handleSubmitForm = async () => {
    if (isSubmitting || !user) return;

    setIsSubmitting(true);
    const newTicket = {
      ticketType: formData.ticketType,
      inquiryType: formData.inquiryType,
      supplier: formData.supplier,
      supplierName: formData.supplierName,
      tsd: formData.tsd,
      tsdName: formData.tsdName,
      firstRun: firstRunDate ? getUTCRunDate(firstRunDate).toISOString() : '',
      receivedDate: receivedDate ? new Date(receivedDate).toISOString() : '',
      status: TicketStatus.NEW,
      proactive: formData.proactive,
      expectedResolutionDate: addDays(new Date(), 60).toISOString(),
      referenceLinkedObjects: formData.referenceLinkedObjects
    };
    const associations: Association[] = association ? [association] : [];

    if (newTicket.referenceLinkedObjects) buildAssociations(associations);

    const { data, error } = await createTicket(newTicket, associations);
    setIsSubmitting(false);

    if (data) {
      setSnack({ message: 'Ticket was created successfully', type: 'success', open: true });
      clearDrawerState();
      navigate(`/ticket/${data.id}`, { viewTransition: true });
    } else if (error) {
      setSnack({ message: `There was an error creating ticket: \n${error.message}`, type: 'error', open: true });
    }
  };

  const handleAssetPrefill = async () => {
    const assetData = objectData as Asset;
    if (assetData) {
      const { tsd, supplierId, supplierName } = assetData;
      let tsdMap: TsdMap | null;

      if (tsd) tsdMap = getTsdMapBySfTsdName(tsd);

      fetchRpmAccountsByAsset(assetData.id, drawer);

      setFormData((prevData) => ({
        ...prevData,
        supplier: supplierId || '',
        supplierName: supplierName || '',
        tsd: tsdMap?.tsd_id ?? '',
        tsdName: tsdMap?.tsd_name ?? ''
      }));
    }
  };

  const handlePAAssetPrefill = async () => {
    const paAssetData = objectData as ProvisionalAsset;
    if (paAssetData) {
      const { tsd, supplier = '', supplierName } = paAssetData;
      let tsdMap: TsdMap | null;

      if (tsd) tsdMap = getTsdMapBySfTsdId(tsd);

      fetchRpmAccountsByPAsset(paAssetData.id, drawer);

      setFormData((prevData) => ({
        ...prevData,
        supplier: supplier || '',
        supplierName: supplierName || '',
        tsd: tsdMap?.tsd_id ?? '',
        tsdName: tsdMap?.tsd_name ?? ''
      }));
    }
  };

  const handleRpmAccountPrefill = async () => {
    const rpmData = objectData as RpmAccount;
    if (rpmData) {
      const { supplierId, source } = rpmData;
      const supplierMap = await fetchSupplierMapsByRpmAcctSupplierId(supplierId);
      const supOp = supplierMap?.map((s: SupplierMap) => ({ label: s.rpm_supplier_name, value: s.supplier_id })) || [];
      setSupplierOptions(supOp);
      let tsdMap: TsdMap | null = null;

      if (source) tsdMap = getTsdMapByRpmSource(source);

      fetchAssetsByRpmAcctId(rpmData.accountId, drawer)();
      fetchProvisionalAssetsByRpmAcctId(rpmData.accountId, drawer)();

      setFormData((prevData) => ({
        ...prevData,
        tsd: tsdMap?.tsd_id ?? '',
        tsdName: tsdMap?.tsd_name ?? '',
        ...(supOp.length === 1 && { supplier: supOp[0].value, supplierName: supOp[0].label })
      }));
    }
  };

  const handlePreFill = async () => {
    setLoading(true);

    switch (objectType) {
      case 'asset':
        await handleAssetPrefill();
        break;
      case 'provisionalAsset':
        await handlePAAssetPrefill();
        break;
      case 'rpmAccount':
        await handleRpmAccountPrefill();
        break;
      default:
        break;
    }

    const latestRun = getRpmRunSubject().getValue();
    if (latestRun && latestRun.runLastDayOfMonth) {
      setFirstRunDate(getUTCRunDate(latestRun.runLastDayOfMonth));

      setFormData((prevData) => ({
        ...prevData,
        firstRun: `${latestRun.runMonthAbbreviation} ${latestRun.runYear}`
      }));
    }

    setLoading(false);
  };

  useEffect(() => {
    const getRpmAccountsByAsset = drawer ? getCruxRpmAccountsByAssetDrawerSubject : getCruxRpmAccountsByAssetSubject;
    const rpmAcctSub = getRpmAccountsByAsset().subscribe((r) => setOtherRpmAssociations(r.data || []));
    const getAssets = drawer ? getAssetsByRpmAcctDrawerSub : getAssetsByRpmAcctSub;
    const asstSub = getAssets().subscribe((r) => setOtherAssetAssociations(r.data || []));
    const getProvisionalAssets = drawer ? getProvisionalAssetsByRpmAcctDrawerSub : getProvisionalAssetsByRpmAcctSub;
    const paSub = getProvisionalAssets().subscribe((r) => setOtherPAAssociations(r.data || []));
    const getRpAcctByPa = drawer ? getTallyRpmAccountsByPAssetDrawerSubject : getTallyRpmAccountsByPAssetSubject;
    const rpAcctByPa = getRpAcctByPa().subscribe((r) => setOtherRpmAssociations(r.data || []));

    fetchTsdMaps();

    return () => {
      if (rpmAcctSub) rpmAcctSub.unsubscribe();
      if (asstSub) asstSub.unsubscribe();
      if (paSub) paSub.unsubscribe();
      if (rpAcctByPa) rpAcctByPa.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setFormData({
      ticketType: TicketType.ASSETS_AND_RPM_ACCOUNTS,
      inquiryType: TicketInquiryType.ACCOUNT_DROPPED_OFF,
      supplier: '',
      supplierName: '',
      tsd: '',
      tsdName: '',
      firstRun: null,
      receivedDate: new Date().toISOString(),
      proactive: true,
      referenceLinkedObjects: true
    });
    if (open) handlePreFill();
  }, [open]);

  useEffect(() => {
    setShowSupplierSearch(objectType !== 'rpmAccount' || (objectType === 'rpmAccount' && !supplierOptions.length));
    setShowSupplierSelect(objectType === 'rpmAccount' && !!supplierOptions.length);
  }, [objectType, supplierOptions.length]);

  return (
    <Dialog
      data-testid="new-ticket-dialog"
      open={open}
      onClose={handleClose}>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          New Ticket
        </TextBlock>
      </DialogTitle>
      {loading && (
        <ComponentSpinner
          label="Loading Data..."
          sx={{ margin: 'auto', height: '23rem', width: '23rem' }}
        />
      )}
      {!loading && (
        <Fragment>
          <DialogContent sx={{ minHeight: 70, width: '36rem' }}>
            <div className="space-y-3 flex">
              <TicketFormFields
                firstRunDate={firstRunDate}
                receivedDate={receivedDate}
                formData={formData}
                isSubmitting={isSubmitting}
                objectType={objectType}
                setFirstRunDate={setFirstRunDate}
                setReceivedDate={setReceivedDate}
                setFormData={setFormData}
                showSupplierSearch={showSupplierSearch}
                showSupplierSelect={showSupplierSelect}
                supplierOptions={supplierOptions}
              />
            </div>
            <div className="mt-3">
              <LinkedReference
                objectType={objectType}
                objectLinkId={objectLinkId}
                objectLinkDisplay={objectLinkDisplay}
                otherRpmAssociations={otherRpmAssociations}
                otherAssetAssociations={otherAssetAssociations}
                otherPAAssociations={otherPAAssociations}
                formData={formData}
                setFormData={setFormData}
                isSubmitting={isSubmitting}
                handleClose={handleClose}
              />
            </div>
          </DialogContent>
          <hr className="mb-1 mx-6" />
          <DialogActions sx={{ marginRight: 2, pb: 2 }}>
            <button
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              onClick={handleClose}>
              Cancel
            </button>
            {isSubmitting ? (
              <div className="flex">
                <ComponentSpinner
                  sx={{ mt: '0', m: '0' }}
                  label="Creating..."
                />
              </div>
            ) : (
              <button
                data-testid="submit-new-link"
                disabled={!formData.supplierName || !formData.tsdName || isSubmitting}
                onClick={handleSubmitForm}
                className={DEFAULT_ADVANCE_BUTTON_STYLE}
                type="submit">
                Save
              </button>
            )}
          </DialogActions>
        </Fragment>
      )}
    </Dialog>
  );
};
